<template>
  <div class="document-classification-view">
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <div>Analyzing document...</div>
    </div>
    
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <div class="error-message">{{ error }}</div>
      <button @click="goBack" class="back-button">Go Back</button>
    </div>
    
    <div v-else class="classification-container">
      <DocumentClassifier
        :document-id="documentId"
        :document-image="documentImage"
        :initial-classification="classification"
        @save="handleSave"
        @cancel="goBack"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DocumentClassifier from '@/components/document-classification/DocumentClassifier.vue';
import documentClassificationService from '@/services/documentClassificationService';
import documentReviewService from '@/services/documentReviewService';
import { useErrorStore } from '@/stores/error_store';

const route = useRoute();
const router = useRouter();
const errorStore = useErrorStore();

const documentId = ref(route.params.id);
const documentImage = ref('');
const classification = ref({
  type: null,
  confidence: 0,
  alternatives: []
});
const loading = ref(true);
const error = ref(null);

onMounted(async () => {
  try {
    // Fetch document image
    documentImage.value = documentReviewService.getDocumentImageUrl(documentId.value);
    
    // Fetch or generate document classification
    await fetchDocumentClassification();
  } catch (err) {
    console.error('Error loading document classification:', err);
    error.value = 'Failed to analyze document. Please try again later.';
  } finally {
    loading.value = false;
  }
});

async function fetchDocumentClassification() {
  try {
    // Try to get existing classification first
    const existingClassification = await documentClassificationService.getClassification(documentId.value);
    if (existingClassification && existingClassification.type) {
      classification.value = existingClassification;
      return;
    }
    
    // If no existing classification, classify the document
    const result = await documentClassificationService.classifyDocument(documentId.value);
    
    // Format the classification result
    classification.value = {
      type: result.type,
      typeName: documentClassificationService.getDocumentType(result.type).name,
      confidence: result.confidence,
      alternatives: result.alternatives.map(alt => ({
        name: documentClassificationService.getDocumentType(alt.type).name,
        id: alt.type,
        confidence: alt.confidence
      }))
    };
  } catch (err) {
    console.error('Error classifying document:', err);
    // Provide a default classification if the API fails
    classification.value = {
      type: 'unknown',
      typeName: 'Unknown Document Type',
      confidence: 0,
      alternatives: []
    };
  }
}

async function handleSave(data) {
  try {
    loading.value = true;
    
    // Save the classification
    await documentClassificationService.saveClassification(documentId.value, data.classification);
    
    // Show success message
    errorStore.setError('Classification Saved', 'Document classification has been saved successfully.', 'success', true);
    
    // Navigate to the document review page
    router.push(`/document-review/${documentId.value}`);
  } catch (err) {
    console.error('Error saving classification:', err);
    errorStore.setError('Classification Error', 'Failed to save document classification.', 'error', false);
  } finally {
    loading.value = false;
  }
}

function goBack() {
  router.back();
}
</script>

<style scoped>
.document-classification-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.loading-container, .error-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #0366d6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 48px;
  margin-bottom: 1rem;
}

.error-message {
  color: #d73a49;
  margin-bottom: 1rem;
}

.back-button {
  padding: 8px 16px;
  background-color: #0366d6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.classification-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>